import { quietHoursMinutes } from "@healthos/shared";
import { z } from "zod";
// Codec for the onboarding_profiles.data JSONB blob. Every key the wizard,
// Settings page, seeds, or demo fills write is enumerated here; readers go
// through parseOnboardingProfileData instead of casting Record<string, unknown>.
// Each field salvages independently (.catch(undefined)) so one corrupt key
// reads as absent without nuking the rest, and unknown keys pass through so
// read-modify-write callers never drop data they don't understand.
const salvage = (schema) => schema.optional().catch(undefined);
export const QuietHoursWindowSchema = z.object({
    start: z.string(),
    end: z.string(),
});
export const OnboardingIdentityDataSchema = z.object({
    becoming: salvage(z.string()),
    destination: salvage(z.string()),
    vow: salvage(z.string()),
});
export const ComposedJourneyDataSchema = z.object({
    version: salvage(z.string()),
    outcomeIds: z.array(z.string()).catch([]),
    behaviorFocus: z.array(z.string()).catch([]),
    lifeContext: z.array(z.string()).catch([]),
    medicalContext: z.array(z.string()).catch([]),
    /** Medication names the member picked, resolved to classes by `medicationClassFor`. */
    medications: z.array(z.string()).catch([]),
    /** False when the member has turned weight trends and weighing prompts off. */
    weightSurfaces: salvage(z.boolean()),
    freeContext: z.string().nullable().catch(null),
    reviewedAt: z.string().nullable().catch(null),
});
export const NotificationChannelsSchema = z.object({
    browser: salvage(z.boolean()),
    teams: salvage(z.boolean()),
    slack: salvage(z.boolean()),
    desktop: salvage(z.boolean()),
    mobile: salvage(z.boolean()),
});
export const OnboardingTrackingSchema = z.object({
    // Nullable: the tracker PUT writes null to clear a previously set value.
    heightCm: salvage(z.number().nullable()),
    goalWeightKg: salvage(z.number().nullable()),
    weightUnit: salvage(z.enum(["kg", "lb"])),
    doseReminders: salvage(z.boolean()),
});
export const WeatherLocationOverrideSchema = z.object({
    lat: z.number(),
    lon: z.number(),
    label: z.string(),
});
export const OnboardingDemographicSchema = z.object({
    ageRange: salvage(z.string()),
    sex: salvage(z.string()),
    gender: salvage(z.string()),
    workerType: salvage(z.string()),
    industry: salvage(z.string()),
});
export const OnboardingPreferencesSchema = z.object({
    paused: salvage(z.boolean()),
    pauseUntil: salvage(z.string()),
    denyFamilies: salvage(z.array(z.string())),
    onlyAfter: salvage(z.string()),
    onlyBefore: salvage(z.string()),
});
export const MobileCompanionStateSchema = z.object({
    connectedAt: salvage(z.string()),
    lastSeenAt: salvage(z.string()),
});
export const GoalTargetDataSchema = z.object({
    value: z.number(),
    unit: z.string(),
    byDate: salvage(z.string()),
});
/** Auto-resolved place from IP geolocation, persisted by the ambient path on
 *  app load. Carries the timezone the member actually sees in Settings. */
export const LocationHintSchema = z.looseObject({
    lat: salvage(z.number()),
    lon: salvage(z.number()),
    timezone: salvage(z.string()),
});
// Persona slugs stay z.string(): personas are config (manifests + registry),
// and the registry, not this codec, is the authority on which slugs exist.
export const OnboardingProfileDataSchema = z.looseObject({
    firstName: salvage(z.string()),
    timezone: salvage(z.string()),
    locationHint: salvage(LocationHintSchema),
    persona: salvage(z.string().nullable()),
    personaIds: salvage(z.array(z.string())),
    personaPinOverride: salvage(z.array(z.string())),
    /** Legacy level-up watermark. New delivery claims live in focus_area_level_events. */
    focusAreaLevels: salvage(z.record(z.string(), z.number())),
    lensMode: salvage(z.enum(["pick", "compose"])),
    lifeContext: salvage(z.string()),
    composedJourney: salvage(ComposedJourneyDataSchema),
    identity: salvage(OnboardingIdentityDataSchema),
    assessment: salvage(z.record(z.string(), z.number())),
    startingLevel: salvage(z.enum(["scattered", "getting_by", "fairly_steady"]).nullable()),
    goalNearHorizon: salvage(z.string().nullable()),
    goalNorthStar: salvage(z.string().nullable()),
    goalNearHorizonText: salvage(z.string()),
    goalNorthStarText: salvage(z.string()),
    currentWeight: salvage(z.number().nullable()),
    goalWantsTarget: salvage(z.boolean().nullable()),
    goalTarget: salvage(GoalTargetDataSchema.nullable()),
    baselineRhythm: salvage(z.array(z.string())),
    baselineHabits: salvage(z.array(z.string())),
    baselineMotivation: salvage(z.array(z.string())),
    baselineMotivationText: salvage(z.string()),
    baselineConstraints: salvage(z.array(z.string())),
    baselineSeason: salvage(z.array(z.string())),
    glp1Started: salvage(z.enum(["yes", "no", "considering"]).nullable()),
    glp1Medication: salvage(z.string().nullable()),
    glp1MedicationName: salvage(z.string().nullable()),
    glp1DoseMg: salvage(z.number().nullable()),
    glp1DoseDay: salvage(z.number().nullable()),
    glp1CadenceDays: salvage(z.number().nullable()),
    glp1StartDate: salvage(z.string().nullable()),
    // Member-reported side effects; empty means none, null means not asked.
    glp1SideEffects: salvage(z.array(z.string()).nullable()),
    channels: salvage(NotificationChannelsSchema),
    quietHours: salvage(QuietHoursWindowSchema),
    // Interaction sounds. localStorage alone cannot hold this in the desktop
    // sidecar (the cross-origin iframe's storage does not survive a relaunch),
    // so the profile is the durable copy. Absent means never explicitly set.
    sound: salvage(z.boolean()),
    // Notification chime (attention toasts and the ambient attention surface).
    // Independent of `sound`: neither toggle mutes the other. Same durability
    // story as `sound`; absent means never explicitly set.
    notificationSound: salvage(z.boolean()),
    // Measurement system: resolved from the browser locale until the user
    // overrides it in Settings; the stored value wins once set.
    units: salvage(z.enum(["metric", "us"])),
    // Coming-soon connectors the user asked to hear about.
    connectorWaitlist: salvage(z.array(z.string())),
    // Legacy nested path for the quiet-hours window; new writes are top-level.
    cadencePrefs: salvage(z.object({ quietHours: salvage(QuietHoursWindowSchema) })),
    npaConsentedAt: salvage(z.string().nullable()),
    npaConsentRecordedAt: salvage(z.string()),
    betaConsentAt: salvage(z.string().nullable()),
    signalSource: salvage(z.string().nullable()),
    tracking: salvage(OnboardingTrackingSchema),
    // Legacy key: no current writer, but old rows may still carry it.
    goals: salvage(z.object({ targetWeightKg: salvage(z.number()) })),
    weatherLocationOverride: salvage(WeatherLocationOverrideSchema.nullable()),
    companionAppPromptDismissedAt: salvage(z.string()),
    mobileCompanion: salvage(MobileCompanionStateSchema),
    demographic: salvage(OnboardingDemographicSchema),
    preferences: salvage(OnboardingPreferencesSchema),
    // Rules mode: opt-in per member, never a default. Absent means never asked.
    // The separate, explained title scope. Structure-only forecasting works without it.
    ruleReadTitles: salvage(z.boolean()),
    teamsTenant: salvage(z.boolean()),
    learnV2: salvage(z.record(z.string(), z.unknown())),
    seededAt: salvage(z.string()),
});
export function parseOnboardingProfileData(raw) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return OnboardingProfileDataSchema.parse({});
    }
    const result = OnboardingProfileDataSchema.safeParse(raw);
    return result.success ? result.data : OnboardingProfileDataSchema.parse({});
}
// Single resolution point for the quiet-hours window: the legacy nested
// cadencePrefs path wins over the top-level key, matching the lens behaviour.
export function quietHoursWindow(data) {
    const window = data.cadencePrefs?.quietHours ?? data.quietHours;
    return window ?? null;
}
function validQuietHours(window) {
    if (!window)
        return null;
    const valid = quietHoursMinutes(window.start) !== null && quietHoursMinutes(window.end) !== null;
    return valid ? { start: window.start, end: window.end } : null;
}
// The one order every gate and clock uses; each source is validated on its own so a bad one falls through.
export function resolveQuietHours(data, personaDefault) {
    const user = validQuietHours(data.cadencePrefs?.quietHours) ?? validQuietHours(data.quietHours);
    if (user)
        return { ...user, source: "user" };
    const persona = validQuietHours(personaDefault);
    return persona ? { ...persona, source: "persona" } : null;
}
/**
 * Single resolution point for the member's clock: the explicitly set profile
 * timezone, then the auto-resolved place (the timezone Settings shows), then
 * the identity row. Every delivery gate and member-facing clock must use this
 * order, or Settings promises one window while the gate enforces another.
 */
export function resolveMemberTimezone(data, identityTimezone) {
    return ((data.timezone?.trim() ? data.timezone.trim() : null) ??
        (data.locationHint?.timezone?.trim() ? data.locationHint.timezone.trim() : null) ??
        (identityTimezone?.trim() ? identityTimezone.trim() : null) ??
        "UTC");
}
// Life-season ids that mean the member's sleep is broken by circumstances
// outside their control, not by a poor routine: a new baby or young child
// waking them through the night (feeds, sleep regressions, teething, night
// wakings, separation anxiety). This continues well past the newborn months,
// so it is deliberately not age-gated. Ids mirror the onboarding season
// catalog (clients/.../journey-builder/life-context-catalog.ts) and the
// compose-flow lifeContext values; both underscore and hyphen forms are
// normalised before lookup. Extend this set as other low-agency seasons are
// grounded.
export const LOW_AGENCY_SEASON_IDS = new Set(["new_parent"]);
function normalizeSeasonId(id) {
    return id.trim().toLowerCase().replace(/-/g, "_");
}
/**
 * Single resolution point for the member's effective life context. Returns
 * "low_agency" when sleep is broken by circumstances they don't control, which
 * routes sleep interpretation into survival framing (5h target, "that's not on
 * you", no hygiene prescriptions) and steers the microstep generator away from
 * routine fixes. The explicit onboarding toggle wins; otherwise a low-agency
 * life season (e.g. "New parent") from the wizard's season question or the
 * compose flow implies it. This is the seam that connects the descriptive
 * season a member picks to the behavioural switch, so a real new parent gets
 * survival framing without the demo seeder having to force the flag.
 */
export function resolveLifeContext(data) {
    if (data.lifeContext === "low_agency")
        return "low_agency";
    const seasons = [...(data.baselineSeason ?? []), ...(data.composedJourney?.lifeContext ?? [])];
    if (seasons.some((id) => LOW_AGENCY_SEASON_IDS.has(normalizeSeasonId(id)))) {
        return "low_agency";
    }
    return "default";
}
export function mergeOnboardingProfileData(existing, patch) {
    const base = existing !== null && typeof existing === "object" && !Array.isArray(existing)
        ? existing
        : {};
    return parseOnboardingProfileData({ ...base, ...patch });
}
//# sourceMappingURL=onboarding-profile.js.map